import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { COMPANY } from "@/data/products";
import {
  ArrowRight,
  MapPin,
  ShieldCheck,
  Layers,
  Mountain,
  Gem,
  FileCheck2,
  Handshake,
  Lock,
  Send,
  CheckCircle2,
  Mail,
  MessageCircle,
  Phone,
  Globe2,
  ScrollText,
  Building2,
} from "lucide-react";

const HERO_IMAGE =
  "https://images.pexels.com/photos/1078884/pexels-photo-1078884.jpeg?auto=compress&cs=tinysrgb&w=2400";

const MINERALS = [
  {
    icon: Layers,
    name: "Copper",
    tag: "Primary target",
    body: "Situated within the Zambian Copperbelt — the most prolific copper province in Africa, hosting Konkola, Nchanga, Mopani and Kansanshi.",
  },
  {
    icon: Mountain,
    name: "Silica",
    tag: "Industrial mineral",
    body: "High-purity silica occurrences suitable for glass, foundry, ferrosilicon and photovoltaic-grade feedstock markets.",
  },
  {
    icon: Gem,
    name: "Quartz",
    tag: "Industrial mineral",
    body: "Vein and pegmatite-hosted quartz — a feedstock for electronics, semiconductor and high-tech glass supply chains.",
  },
  {
    icon: Gem,
    name: "Emerald",
    tag: "Gemstone by-product",
    body: "Zambia is the world's second-largest source of emerald by value. Historical emerald indications add material upside optionality.",
  },
];

const THESIS = [
  {
    icon: Globe2,
    n: "01",
    title: "Copperbelt Location",
    body: "Lufwanyama and Luanshya sit at the heart of the Zambian Copperbelt — one of the world's largest and highest-grade copper provinces, with existing infrastructure, power and skilled labour.",
  },
  {
    icon: FileCheck2,
    n: "02",
    title: "Large-Scale Licence",
    body: "The asset holds a Large-Scale Exploration Licence under Zambian mining law — issued by the Ministry of Mines & Minerals Development, granting extensive rights to explore, prove and progress toward mining.",
  },
  {
    icon: Layers,
    n: "03",
    title: "Multi-Mineral Optionality",
    body: "Confirmed presence of copper alongside silica, quartz and emerald — offering rare polymetallic upside within a single licence area, materially de-risking commodity concentration.",
  },
  {
    icon: ShieldCheck,
    n: "04",
    title: "Deal-Structure Flexibility",
    body: "Open to outright sale, farm-in / earn-in joint venture, project-finance backed development, or strategic partnership with a mining major.",
  },
];

const STRUCTURES = [
  {
    tag: "Option A",
    title: "Outright Sale",
    body: "100% acquisition of the licence-holding entity and associated rights. Clean cap-table, full operational control transferred at completion.",
    ideal: "Ideal for mining majors, sovereign funds, private-equity mining vehicles.",
  },
  {
    tag: "Option B",
    title: "Joint Venture / Earn-In",
    body: "Farm-in against staged exploration and development spend to earn up to 70%. Local partner retains carried interest and operational continuity.",
    ideal: "Ideal for exploration companies and strategic operators.",
  },
  {
    tag: "Option C",
    title: "Strategic Partnership",
    body: "Development capital plus offtake in exchange for equity and long-dated purchase rights against future production.",
    ideal: "Ideal for smelters, refiners, integrated commodity traders.",
  },
];

const CHECKLIST = [
  "Certified copies of the Large-Scale Exploration Licence",
  "Cadastral maps and coordinates of the licence area",
  "Historical geological reports, sampling & assay data",
  "Environmental & social baseline documentation",
  "Access, water, power and logistics assessments",
  "Corporate structure, KYC and beneficial-ownership file",
];

const initial = {
  name: "",
  email: "",
  phone: "",
  company: "",
  country: "",
  ticket: "",
  interest: "",
  message: "",
};

export default function MineForSale() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const composed =
        `[MINE-FOR-SALE / JV ENQUIRY]\n` +
        `Company: ${form.company || "—"}\n` +
        `Country: ${form.country || "—"}\n` +
        `Ticket / Structure: ${form.ticket || "—"}\n` +
        `Interest: ${form.interest || "—"}\n\n` +
        `${form.message}`;

      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone || undefined,
        message: composed,
      };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === undefined) delete payload[k];
      });
      await api.post("/inquiries", payload);
      toast.success(
        "Enquiry received. Our transaction desk will contact qualified parties under NDA."
      );
      setSubmitted(true);
      setForm(initial);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Could not submit. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Validation error.");
    } finally {
      setLoading(false);
    }
  };

  const waMessage = encodeURIComponent(
    "Hello EELLEVEN MINING, I'm interested in the Lufwanyama / Luanshya mining asset for sale."
  );

  return (
    <div data-testid="page-mine-for-sale">
      {/* HERO */}
      <section className="relative min-h-[86vh] overflow-hidden border-b border-[var(--eel-border)]">
        <img
          src={HERO_IMAGE}
          alt="Copperbelt mining asset"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--eel-bg)] via-[var(--eel-bg)]/85 to-[var(--eel-bg)]/40" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[var(--eel-bg)]" />

        <div className="relative min-h-[86vh] max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-center py-24">
          <div className="flex items-center gap-3 reveal">
            <span className="w-8 h-px copper-bg" />
            <p className="label-eyebrow text-[var(--eel-copper)]">
              Confidential · For qualified international buyers
            </p>
          </div>

          <h1
            data-testid="mfs-hero-title"
            className="font-editorial mt-8 text-5xl md:text-7xl lg:text-[92px] leading-[0.98] text-white max-w-5xl reveal reveal-delay-1"
          >
            A copperbelt asset,
            <br />
            <span className="font-editorial-italic copper-text">
              for outright sale or JV.
            </span>
          </h1>

          <p className="mt-8 max-w-2xl text-base md:text-lg text-zinc-200 leading-relaxed font-light reveal reveal-delay-2">
            EELLEVEN MINING is inviting expressions of interest from
            international mining houses, funds and strategic partners for a
            large-scale exploration asset located in{" "}
            <span className="text-white font-medium">
              Lufwanyama and Luanshya, on the Zambian Copperbelt.
            </span>{" "}
            Multi-mineral endowment. Licence in good standing.
          </p>

          {/* Fact strip */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 border border-white/10 bg-black/30 backdrop-blur-sm reveal reveal-delay-3">
            {[
              ["Location", "Lufwanyama · Luanshya"],
              ["Region", "Copperbelt · Zambia"],
              ["Licence", "Large-Scale Exploration"],
              ["Minerals", "Cu · SiO₂ · Quartz · Emerald"],
            ].map(([k, v], i) => (
              <div
                key={i}
                data-testid={`mfs-fact-${i}`}
                className={`px-6 py-5 ${
                  i < 3 ? "md:border-r border-white/10" : ""
                } ${i < 2 ? "border-b md:border-b-0 border-white/10" : ""} ${
                  i === 2 ? "border-b md:border-b-0 border-white/10" : ""
                }`}
              >
                <p className="label-eyebrow text-zinc-400">{k}</p>
                <p className="mt-2 font-editorial text-lg md:text-xl text-white leading-tight">
                  {v}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-3 reveal reveal-delay-3">
            <a
              href="#enquire"
              data-testid="mfs-cta-datroom"
              className="px-7 py-4 copper-bg text-black font-semibold uppercase tracking-widest text-xs hover:bg-[var(--eel-copper-hover)] transition-colors inline-flex items-center gap-3"
            >
              Request Data Room <ArrowRight size={14} />
            </a>
            <a
              href={`https://wa.me/${COMPANY.whatsappPrimaryDigits}?text=${waMessage}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mfs-cta-whatsapp-ie"
              className="px-6 py-4 border border-white/40 text-white font-semibold uppercase tracking-widest text-xs hover:bg-white hover:text-black transition-colors inline-flex items-center gap-3"
            >
              <MessageCircle size={14} className="text-green-400" />
              WhatsApp IE · {COMPANY.whatsappPrimary}
            </a>
            <a
              href={`https://wa.me/${COMPANY.phoneZambiaDigits}?text=${waMessage}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mfs-cta-whatsapp-zm"
              className="px-6 py-4 border border-white/40 text-white font-semibold uppercase tracking-widest text-xs hover:bg-white hover:text-black transition-colors inline-flex items-center gap-3"
            >
              <MessageCircle size={14} className="text-green-400" />
              WhatsApp ZM · {COMPANY.phoneZambia}
            </a>
            <a
              href={`https://wa.me/${COMPANY.phoneZambia2Digits}?text=${waMessage}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mfs-cta-whatsapp-zm2"
              className="px-6 py-4 border border-white/40 text-white font-semibold uppercase tracking-widest text-xs hover:bg-white hover:text-black transition-colors inline-flex items-center gap-3"
            >
              <MessageCircle size={14} className="text-green-400" />
              WhatsApp ZM · {COMPANY.phoneZambia2}
            </a>
          </div>
        </div>
      </section>

      {/* THE ASSET / OVERVIEW */}
      <section
        className="border-b border-[var(--eel-border)]"
        data-testid="mfs-overview"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <p className="label-eyebrow">/ The Asset</p>
            <h2 className="font-editorial mt-4 text-4xl md:text-5xl lg:text-6xl leading-[1.03] text-white">
              A rare polymetallic
              <span className="font-editorial-italic copper-text">
                {" "}
                Copperbelt position.
              </span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-zinc-300 leading-relaxed font-light text-base md:text-lg">
            <p>
              The asset comprises an active{" "}
              <span className="text-white font-medium">
                Large-Scale Exploration Licence
              </span>{" "}
              covering ground in the Lufwanyama and Luanshya districts of
              Zambia&apos;s Copperbelt — a region that has produced tens of millions
              of tonnes of copper for over a century and remains at the centre
              of Africa&apos;s mining renaissance.
            </p>
            <p>
              On-site mineral occurrences confirmed to date include{" "}
              <span className="text-white font-medium">copper</span>, together
              with industrial-mineral occurrences of{" "}
              <span className="text-white font-medium">silica</span> and{" "}
              <span className="text-white font-medium">quartz</span>, and
              gemstone potential in the form of{" "}
              <span className="text-white font-medium">emerald</span> — the
              country&apos;s second-largest export by value after copper.
            </p>
            <p>
              EELLEVEN MINING is opening the asset to international parties
              through an outright sale process, a farm-in / earn-in joint
              venture, or a strategic partnership backed by offtake. Full data
              room access is released under NDA to qualified counterparties.
            </p>
          </div>
        </div>
      </section>

      {/* MINERAL ENDOWMENT */}
      <section
        className="section-paper border-b border-[var(--eel-paper-2)]"
        data-testid="mfs-minerals"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <p className="label-eyebrow">/ On-site minerals</p>
              <h2 className="font-editorial mt-3 text-4xl md:text-5xl lg:text-6xl leading-[1.04] text-[var(--eel-ink)]">
                Four commodities.
                <span className="font-editorial-italic copper-text">
                  {" "}
                  One licence.
                </span>
              </h2>
            </div>
            <p className="max-w-md text-sm text-[var(--eel-ink-muted)] leading-relaxed">
              Independent verification is available in the data room, including
              historical sampling, assay results and satellite geology.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--eel-paper-2)] border border-[var(--eel-paper-2)]">
            {MINERALS.map((m, i) => {
              const Icon = m.icon;
              return (
                <div
                  key={m.name}
                  data-testid={`mfs-mineral-${i}`}
                  className="bg-white p-8 group"
                >
                  <div className="flex items-baseline justify-between">
                    <Icon size={30} className="copper-text" />
                    <span className="label-eyebrow text-zinc-500">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="mt-8 font-editorial text-3xl text-[var(--eel-ink)] leading-tight">
                    {m.name}
                  </h3>
                  <p className="mt-2 label-eyebrow text-[var(--eel-copper)]">
                    {m.tag}
                  </p>
                  <p className="mt-5 text-sm text-[var(--eel-ink-muted)] leading-relaxed">
                    {m.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* INVESTMENT THESIS */}
      <section
        className="border-b border-[var(--eel-border)]"
        data-testid="mfs-thesis"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <p className="label-eyebrow">/ Why this asset</p>
          <h2 className="font-editorial mt-3 text-4xl md:text-5xl lg:text-6xl leading-[1.04] text-white max-w-4xl">
            Four reasons global mining
            <span className="font-editorial-italic copper-text">
              {" "}
              houses should engage.
            </span>
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-800 border border-zinc-800">
            {THESIS.map((t, i) => {
              const Icon = t.icon;
              return (
                <div
                  key={i}
                  data-testid={`mfs-thesis-${i}`}
                  className="bg-[var(--eel-surface)] p-8 md:p-10 group"
                >
                  <div className="flex items-baseline justify-between">
                    <Icon size={28} className="copper-text" />
                    <span className="font-heading text-4xl md:text-5xl font-black text-zinc-800 group-hover:text-zinc-600 transition-colors">
                      {t.n}
                    </span>
                  </div>
                  <h3 className="mt-8 font-editorial text-2xl md:text-3xl text-white leading-tight">
                    {t.title}
                  </h3>
                  <p className="mt-4 text-zinc-400 leading-relaxed font-light">
                    {t.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* DEAL STRUCTURES */}
      <section
        className="section-paper border-b border-[var(--eel-paper-2)]"
        data-testid="mfs-structures"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <p className="label-eyebrow">/ Transaction options</p>
          <h2 className="font-editorial mt-3 text-4xl md:text-5xl lg:text-6xl leading-[1.04] text-[var(--eel-ink)] max-w-4xl">
            Three ways to
            <span className="font-editorial-italic copper-text"> engage.</span>
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {STRUCTURES.map((s, i) => (
              <div
                key={i}
                data-testid={`mfs-structure-${i}`}
                className="bg-white border border-[var(--eel-paper-2)] p-8 flex flex-col"
              >
                <p className="label-eyebrow text-[var(--eel-copper)]">{s.tag}</p>
                <h3 className="mt-3 font-editorial text-3xl text-[var(--eel-ink)] leading-tight">
                  {s.title}
                </h3>
                <p className="mt-5 text-sm text-[var(--eel-ink-muted)] leading-relaxed">
                  {s.body}
                </p>
                <div className="mt-auto pt-6 border-t border-[var(--eel-paper-2)]">
                  <p className="text-xs text-[var(--eel-ink)] font-medium">
                    {s.ideal}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DATA ROOM CHECKLIST */}
      <section
        className="border-b border-[var(--eel-border)]"
        data-testid="mfs-dataroom"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <p className="label-eyebrow">/ Data Room</p>
            <h2 className="font-editorial mt-3 text-4xl md:text-5xl leading-[1.04] text-white">
              Released under
              <span className="font-editorial-italic copper-text"> NDA.</span>
            </h2>
            <p className="mt-6 text-zinc-400 leading-relaxed font-light">
              Qualified counterparties (mining houses, funds, corporate
              buyers) receive access to a curated data room within two
              business days of NDA execution.
            </p>
            <div className="mt-8 flex items-center gap-3 text-xs text-zinc-500 uppercase tracking-widest">
              <Lock size={14} className="copper-text" />
              Confidential · Vetted parties only
            </div>
          </div>

          <div className="lg:col-span-7">
            <ul className="border border-zinc-800 divide-y divide-zinc-800">
              {CHECKLIST.map((c, i) => (
                <li
                  key={i}
                  data-testid={`mfs-dataroom-${i}`}
                  className="p-5 flex items-start gap-4 group hover:bg-[var(--eel-surface)] transition-colors"
                >
                  <span className="w-8 h-8 grid place-items-center border border-zinc-800 shrink-0 group-hover:border-[var(--eel-copper)] transition-colors">
                    <ScrollText size={14} className="copper-text" />
                  </span>
                  <div>
                    <p className="label-eyebrow text-zinc-500">
                      Item {String(i + 1).padStart(2, "0")}
                    </p>
                    <p className="mt-1 text-zinc-200 font-light">{c}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ENQUIRE / CONTACT */}
      <section
        id="enquire"
        className="scroll-mt-24"
        data-testid="mfs-enquire"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="label-eyebrow">/ Register interest</p>
            <h2 className="font-editorial mt-3 text-4xl md:text-5xl leading-[1.04] text-white">
              Speak to the
              <span className="font-editorial-italic copper-text">
                {" "}
                transaction desk.
              </span>
            </h2>
            <p className="mt-6 text-zinc-400 leading-relaxed font-light">
              All submissions are reviewed under strict confidence. Only
              qualified parties will be contacted for NDA execution and data
              room access.
            </p>

            <div className="mt-10 space-y-3 text-sm">
              <a
                href={`mailto:${COMPANY.email}?subject=Mine%20For%20Sale%20%2F%20JV%20Enquiry`}
                data-testid="mfs-contact-email"
                className="flex items-center gap-3 text-zinc-300 hover:text-[var(--eel-copper)] group"
              >
                <span className="w-10 h-10 grid place-items-center border border-zinc-800 group-hover:border-[var(--eel-copper)] transition shrink-0">
                  <Mail size={14} className="copper-text" />
                </span>
                {COMPANY.email}
              </a>
              <a
                href={`https://wa.me/${COMPANY.whatsappPrimaryDigits}?text=${waMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mfs-contact-wa-ie"
                className="flex items-center gap-3 text-zinc-300 hover:text-[var(--eel-copper)] group"
              >
                <span className="w-10 h-10 grid place-items-center border border-zinc-800 group-hover:border-green-500 transition shrink-0">
                  <MessageCircle size={14} className="text-green-400" />
                </span>
                WhatsApp · Ireland · {COMPANY.whatsappPrimary}
              </a>
              <a
                href={`https://wa.me/${COMPANY.phoneZambiaDigits}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mfs-contact-wa-zm"
                className="flex items-center gap-3 text-zinc-300 hover:text-[var(--eel-copper)] group"
              >
                <span className="w-10 h-10 grid place-items-center border border-zinc-800 group-hover:border-green-500 transition shrink-0">
                  <MessageCircle size={14} className="text-green-400" />
                </span>
                WhatsApp · Zambia · {COMPANY.phoneZambia}
              </a>
              <div className="flex items-center gap-3 text-zinc-300">
                <span className="w-10 h-10 grid place-items-center border border-zinc-800 shrink-0">
                  <Building2 size={14} className="copper-text" />
                </span>
                {COMPANY.address}
              </div>
            </div>

            <Link
              to="/invest"
              data-testid="mfs-link-invest"
              className="mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-widest copper-text link-underline"
            >
              See broader investment programme <ArrowRight size={12} />
            </Link>
          </div>

          <div className="lg:col-span-7">
            <div className="border border-[var(--eel-border)] p-6 md:p-10 bg-[var(--eel-surface)]">
              {submitted ? (
                <div className="py-12 text-center" data-testid="mfs-success">
                  <CheckCircle2 size={56} className="copper-text mx-auto" />
                  <h3 className="font-editorial mt-6 text-3xl">
                    Enquiry Received
                  </h3>
                  <p className="mt-3 text-zinc-400 max-w-md mx-auto font-light">
                    Our transaction desk will review your submission and
                    respond to qualified parties within 3 business days.
                  </p>
                  <button
                    data-testid="mfs-success-new"
                    onClick={() => setSubmitted(false)}
                    className="mt-8 px-6 py-3 border border-[var(--eel-border-2)] text-sm uppercase tracking-widest hover:border-white"
                  >
                    Submit Another
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  className="space-y-6"
                  data-testid="mfs-form"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field
                      id="name"
                      label="Full Name *"
                      value={form.name}
                      onChange={update("name")}
                      required
                      minLength={2}
                      placeholder="Jane Doe"
                    />
                    <Field
                      id="email"
                      type="email"
                      label="Business Email *"
                      value={form.email}
                      onChange={update("email")}
                      required
                      placeholder="jane@company.com"
                    />
                    <Field
                      id="company"
                      label="Company / Fund"
                      value={form.company}
                      onChange={update("company")}
                      placeholder="Acme Mining Ltd."
                    />
                    <Field
                      id="country"
                      label="Country"
                      value={form.country}
                      onChange={update("country")}
                      placeholder="United Arab Emirates"
                    />
                    <Field
                      id="phone"
                      label="Phone / WhatsApp"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="+971 ..."
                    />
                    <div>
                      <label
                        className="label-eyebrow text-zinc-400 block mb-2"
                        htmlFor="ticket"
                      >
                        Ticket / Structure
                      </label>
                      <select
                        id="ticket"
                        data-testid="mfs-input-ticket"
                        value={form.ticket}
                        onChange={update("ticket")}
                        className="w-full bg-[var(--eel-bg)] border border-[var(--eel-border)] px-4 py-3 text-sm text-zinc-100 focus:outline-none"
                      >
                        <option value="">— Select —</option>
                        <option value="Outright Sale">Outright Sale</option>
                        <option value="JV / Earn-In">JV / Earn-In</option>
                        <option value="Strategic Partnership">
                          Strategic Partnership
                        </option>
                        <option value="Data Room Access Only">
                          Data Room Access Only
                        </option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label
                      className="label-eyebrow text-zinc-400 block mb-2"
                      htmlFor="interest"
                    >
                      Primary interest
                    </label>
                    <select
                      id="interest"
                      data-testid="mfs-input-interest"
                      value={form.interest}
                      onChange={update("interest")}
                      className="w-full bg-[var(--eel-bg)] border border-[var(--eel-border)] px-4 py-3 text-sm text-zinc-100 focus:outline-none"
                    >
                      <option value="">— Select —</option>
                      <option value="Copper">Copper</option>
                      <option value="Silica">Silica</option>
                      <option value="Quartz">Quartz</option>
                      <option value="Emerald">Emerald</option>
                      <option value="Whole asset">Whole asset</option>
                    </select>
                  </div>

                  <div>
                    <label
                      className="label-eyebrow text-zinc-400 block mb-2"
                      htmlFor="message"
                    >
                      Message *
                    </label>
                    <textarea
                      id="message"
                      data-testid="mfs-input-message"
                      required
                      minLength={5}
                      rows={5}
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Briefly describe your mandate, timeline and technical questions."
                      className="w-full bg-[var(--eel-bg)] border border-[var(--eel-border)] px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none"
                    />
                  </div>

                  <div className="flex items-start gap-3 text-xs text-zinc-500 leading-relaxed">
                    <Lock size={14} className="copper-text mt-0.5 shrink-0" />
                    Submissions are strictly confidential. Data room access is
                    granted only after NDA execution with qualified parties.
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="mfs-submit"
                    className="w-full md:w-auto px-8 py-4 copper-bg text-black font-semibold uppercase tracking-widest text-xs hover:bg-[var(--eel-copper-hover)] transition-colors inline-flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      "Submitting..."
                    ) : (
                      <>
                        Submit Enquiry <Send size={14} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ id, label, ...rest }) {
  return (
    <div>
      <label className="label-eyebrow text-zinc-400 block mb-2" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        data-testid={`mfs-input-${id}`}
        {...rest}
        className="w-full bg-[var(--eel-bg)] border border-[var(--eel-border)] px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none"
      />
    </div>
  );
}
